/* istanbul ignore file - need to fix flakey tests */
import { useConfiguration } from '@providers/Configuration';
import { useTenant } from '@providers/Tenant';
import { CategoryNode } from '@services/productInventory';
import { createContext, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import { ulid } from 'ulid';
import { usePartsCategoriesData } from '../hooks/usePartsCategoriesData';
import { usePartsCategoriesReducer } from '../hooks/usePartsCategoriesReducer';

interface PartsCategoriesContextValue {
  state: CategoryNode[];
  reset: (data: CategoryNode[]) => void;
  addCategory: (name: string) => void;
  updateCategory: (updatedNode: CategoryNode) => void;
  deleteCategory: (nodeToDelete: CategoryNode) => void;
  moveCategory: (nodesToMove: CategoryNode[], targetNode: CategoryNode) => void;
}

const PartsCategoriesContext = createContext<PartsCategoriesContextValue | undefined>(undefined);

interface PartsCategoriesProviderProps {
  children: ReactNode;
  configurationPID: string;
}

export function PartsCategoriesProvider({ children, configurationPID }: PartsCategoriesProviderProps) {
  const { accountId } = useTenant();

  const { parentPID: productPID } = useConfiguration();
  const { data, isSuccess } = usePartsCategoriesData({
    accountId,
    configurationPID,
    productPID,
  });

  const reducer = usePartsCategoriesReducer<CategoryNode>();
  const [state, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    if (isSuccess) {
      dispatch({ type: 'reset', payload: data });
    }
  }, [isSuccess, data]);

  const value = useMemo(() => {
    const reset = () => {
      dispatch({ type: 'reset', payload: data });
    };

    const addCategory = (name: string) => {
      const newCategory: CategoryNode = {
        PID: ulid(),
        name,
        partNumber: '',
        itemType: 'Category',
        isCategory: true,
        children: [],
      };
      dispatch({ type: 'addCategory', newCategory });
    };

    const updateCategory = (updatedNode: CategoryNode) => {
      dispatch({ type: 'update', updatedNode });
    };

    const deleteCategory = (nodeToDelete: CategoryNode) => {
      dispatch({ type: 'delete', nodeToDelete });
    };

    const moveCategory = (nodesToMove: CategoryNode[], targetNode: CategoryNode) => {
      nodesToMove.forEach((nodeToMove) => {
        dispatch({ type: 'move', nodeToMove, targetNode });
      });
    };
    return {
      state,
      reset,
      addCategory,
      updateCategory,
      deleteCategory,
      moveCategory,
    };
  }, [state, data]);

  return <PartsCategoriesContext.Provider value={value}>{children}</PartsCategoriesContext.Provider>;
}

export function usePartsCategories() {
  const context = useContext(PartsCategoriesContext);
  /* istanbul ignore if - should never happen */
  if (context === undefined) {
    throw new Error('usePartsCategories must be used within a PartsCategoriesProvider');
  }

  return context;
}
